import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

// Components
import GatsbyHead from '../../components/layout/gatsby-head';
import Layout, { Container, TwoThirds, SideBar } from '../../components/layout';
import Hero from '../../components/hero';
import RichText from '../../components/rich-text';
import Assemblies from '../../components/assemblies';
import PageTitle from '../../components/page-title';
import { DonationBannerAssembly } from '../../components/donation-banner';
import withSplitTest from '../../components/with-split-test';
import SubpagePagination from '../../components/advice-page/subpage-pagination';

// Styles
import { ContentWithSideBar } from '../../components/styled/containers';

// Utils
import { getComponentOfType } from '../../utils/contentful-components';
import { consistentString } from '../../utils/content-formatting';
import { extractLayoutProps } from '../../utils/layout';
import { useSurvey } from '../../hooks/survey';

export const Head = ({ data }) => <GatsbyHead pageContent={data.page} />;

const ContentPage = ({ data }) => {
  const { page } = data;

  const {
    bodyCopy,
    heroContent,
    assemblies,
    title,
    contentWidth: contentWidthFromCms,
    sidebarAssemblies,
    displayAsGuide,
  } = page;

  const contentWidth = consistentString(contentWidthFromCms);

  const hasHero = heroContent && heroContent[0];

  const donationBannerAssembly = getComponentOfType(
    assemblies,
    'ContentfulComponentDonationBanner'
  );

  const assembliesMinusDonationBanner = assemblies
    ? assemblies.filter(
        (assembly) =>
          assembly?.internal?.type &&
          assembly.internal.type !== 'ContentfulComponentDonationBanner'
      )
    : null;

  const navigation = page.navigation || {};
  const partOfGuide = navigation.up?.page?.displayAsGuide;

  const guidesConfig = {
    paginationPrevious: {
      scotland: navigation.previous,
      // if navigation.previous doesn't exist we must be on the first of the child pages so display the top level page as the previous page
      england: navigation.previous || {
        label: navigation.up?.label,
        path: navigation.up?.path,
      },
    },
  };

  const embeddedSurvey = useSurvey(navigation.path);

  return (
    <Layout {...extractLayoutProps(page, data.contentfulConfig)}>
      {hasHero && <Hero content={heroContent[0]} />}
      {!hasHero && (
        <PageTitle twoThirds>
          <h1>{title}</h1>
        </PageTitle>
      )}
      {(contentWidth === 'two-thirds-with-sidebar' ||
        contentWidth === 'default') && (
        <>
          <Container>
            <ContentWithSideBar>
              <TwoThirds>
                {bodyCopy ? (
                  <article>
                    <RichText richText={bodyCopy} />
                  </article>
                ) : null}

                <Assemblies
                  assemblies={assembliesMinusDonationBanner}
                  insideContainer
                  insideTwoThirds
                />

                {displayAsGuide && !partOfGuide && (
                  <SubpagePagination next={navigation.down?.[0]} />
                )}
                {partOfGuide && (
                  <SubpagePagination
                    next={navigation.next}
                    previous={
                      guidesConfig.paginationPrevious[process.env.SHELTER_SITE]
                    }
                  />
                )}
              </TwoThirds>

              {sidebarAssemblies && (
                <SideBar>
                  <Assemblies assemblies={sidebarAssemblies} />
                </SideBar>
              )}
            </ContentWithSideBar>
            <div id="hotjar-embedded-survey-original" />
            {embeddedSurvey}
          </Container>
          {donationBannerAssembly && (
            <DonationBannerAssembly contentfulData={donationBannerAssembly} />
          )}
        </>
      )}

      {contentWidth === '100%' && (
        <>
          {bodyCopy ? (
            <Container as="article">
              <TwoThirds>
                <RichText richText={bodyCopy} />
              </TwoThirds>
            </Container>
          ) : null}
          <Assemblies assemblies={assemblies} />
          <Container>
            {displayAsGuide && !partOfGuide && (
              <SubpagePagination next={navigation.down?.[0]} />
            )}
            {partOfGuide && (
              <SubpagePagination
                next={navigation.next}
                previous={
                  guidesConfig.paginationPrevious[process.env.SHELTER_SITE]
                }
              />
            )}
          </Container>
          <div id="hotjar-embedded-survey-original" />
          {embeddedSurvey}
        </>
      )}
    </Layout>
  );
};

ContentPage.propTypes = {
  data: PropTypes.shape({
    page: PropTypes.object,
    contentfulConfig: PropTypes.object,
  }),
  pageContext: PropTypes.object,
};

export default withSplitTest(ContentPage);

export const pageQuery = graphql`
  query pageTemplateQuery($contentful_id: String!) {
    page: contentfulPageContent(contentful_id: { eq: $contentful_id }) {
      ...PageContentFragment
    }
    ...NavigationFragment
  }
`;
